import { Box, Checkbox, Divider, Flex, Stack, Text } from '@mantine/core';
import { Client } from 'src/admin/users/types';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useClients } from '../hooks/useClients';
import { isMemberSelected } from './dataHelpers';
import { formatName, NameFormat } from 'src/utils/nameFormatting';

type Props = {
  selectedMembers: Client[];
  onToggle: (client: Client) => void;
  searchTerm?: string;
  noScroll?: boolean;
  excludeInFamilies?: boolean;
};

export default function MemberSearchList({
  selectedMembers,
  onToggle,
  searchTerm,
  noScroll,
  excludeInFamilies,
}: Props) {
  const { clients } = useClients();

  const words = searchTerm?.trim() ? searchTerm.toLowerCase().split(/\s+/).filter(Boolean) : [];

  const filtered = clients.filter(client => {
    if (excludeInFamilies && client.familyId) return false;
    if (words.length === 0) return true;
    const fullName = formatName(
      client.firstName,
      client.lastName,
      NameFormat.NATURAL,
    ).toLowerCase();
    const email = (client.emailAddress ?? '').toLowerCase();
    const address = (client.personalDetails?.address ?? '').toLowerCase();
    return words.every(
      word => fullName.includes(word) || email.includes(word) || address.includes(word),
    );
  });

  return (
    <Box
      p="sm"
      bd="var(--solid-border)"
      style={{
        borderRadius: 12,
        height: noScroll ? undefined : '100%',
        overflowY: noScroll ? undefined : 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--mantine-spacing-xxs)',
      }}
    >
      {selectedMembers.length > 0 && (
        <>
          <Flex wrap="wrap" gap="xxs" pb="sm">
            {selectedMembers.map(client => (
              <Flex
                key={client.id}
                align="center"
                gap="xs"
                px="md"
                py="xs"
                bg="gray.0"
                style={{
                  borderRadius: 20,
                  cursor: 'pointer',
                }}
                onClick={() => onToggle(client)}
              >
                <Checkbox
                  checked
                  onChange={() => onToggle(client)}
                  onClick={e => e.stopPropagation()}
                  radius="sm"
                />
                <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
                  {formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
                </Text>
              </Flex>
            ))}
          </Flex>
          <Divider mx="sm" color="gray.0" size={1} />
        </>
      )}
      {filtered.length === 0 && (
        <Text size="sm" c="gray.5" fw={FONT_WEIGHTS.medium} ta="center" py="md">
          No clients without families
        </Text>
      )}
      {filtered.map(client => {
        const isSelected = isMemberSelected(client, selectedMembers);
        const email = client.emailAddress ?? '';
        const address = client.personalDetails?.address ?? '';
        const subtext = [email, address].filter(Boolean).join(' • ');

        return (
          <Box
            key={client.id}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--mantine-spacing-sm)',
            }}
            onClick={() => onToggle(client)}
          >
            <Checkbox
              checked={isSelected}
              onChange={() => onToggle(client)}
              onClick={e => e.stopPropagation()}
              radius="sm"
              pl="md"
            />
            <Stack gap={0} py="xxs">
              <Text fw={FONT_WEIGHTS.semibold} size="sm" c="gray.10" pb="xxxs">
                {formatName(client.firstName, client.lastName, NameFormat.NATURAL)}
              </Text>
              {subtext && (
                <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7">
                  {subtext}
                </Text>
              )}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}
